import { createBrowserRouter, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { setGlobalNavigator } from '@shared/lib/navigation';
import { AuthShell } from './shell/AuthShell';
import { TabShell } from './shell/TabShell';
import { MorePage } from './shell/MorePage';
import { RouteBannerFrame, type ResidentBannerModuleKey } from '@shared/ui/banners/RouteBannerFrame';
import { useAuthStore } from '@shared/stores/authStore';
import { fetchMyOnboardingGate, onboardingRouteForGate } from '@features/join/api';

import { Welcome } from '@features/welcome/Welcome';
import { WelcomeTour } from '@features/welcome/WelcomeTour';
import { SelectProperty } from '@features/welcome/SelectProperty';
import { Login } from '@features/auth/Login';
import { Signup } from '@features/auth/Signup';
import { ForgotPassword } from '@features/auth/ForgotPassword';
import { ResetPassword } from '@features/auth/ResetPassword';
import { Onboarding } from '@features/onboarding/Onboarding';
import { SurveyRunner } from '@features/surveys/SurveyRunner';

import { PublicAccessInviteAccept } from '@features/access-invites/public/PublicAccessInviteAccept';
import { PublicAccessOnboarding } from '@features/access-invites/public/PublicAccessOnboarding';
import { AccessInvitesList } from '@features/access-invites/owner/AccessInvitesList';
import { MyVisitors } from '@features/access-invites/owner/MyVisitors';
import { NewAccessInvitePage } from '@features/access-invites/owner/NewAccessInvite';

import { Home } from '@features/dashboard/Home';
import { Profile } from '@features/profile/Profile';
import { TokenizationCallback } from '@features/profile/TokenizationCallback';
import { MyProperties } from '@features/properties/MyProperties';
import { MyUtilities } from '@features/utilities/MyUtilities';
import { UtilityDetail } from '@features/utilities/UtilityDetail';
import { PanicSos } from '@features/panic/PanicSos';
import { PanicContactForm } from '@features/panic/PanicContactForm';
import { SupportList } from '@features/support/SupportList';
import { SupportDetail } from '@features/support/SupportDetail';
import { NewSupportRequest } from '@features/support/NewSupportRequest';
import { SupportLiveChat } from '@features/support/SupportLiveChat';
import { MemberRequests } from '@features/member-requests/MemberRequests';
import { Documents } from '@features/documents/Documents';
import { NewsStandList } from '@features/news-stand/NewsStandList';
import { NewsStandDetail } from '@features/news-stand/NewsStandDetail';
import { GalleryLanding } from '@features/gallery/GalleryLanding';
import { GalleryAlbum } from '@features/gallery/GalleryAlbum';
import { Directory } from '@features/directory/Directory';
import { Suppliers } from '@features/suppliers/Suppliers';
import { ContactGroups } from '@features/contact-groups/ContactGroups';
import { Classifieds } from '@features/classifieds/Classifieds';
import { ClassifiedDetail } from '@features/classifieds/ClassifiedDetail';
import { ClassifiedForm } from '@features/classifieds/ClassifiedForm';
import { Events } from '@features/events/Events';
import { MapView } from '@features/map/MapView';
import { Management } from '@features/management/Management';
import { IframePage } from '@features/iframe/IframePage';
import { PdfViewerPage } from '@features/pdf-viewer/PdfViewerPage';
import { Products } from '@features/products/Products';
import { ProductDetail } from '@features/products/ProductDetail';
import { Cart } from '@features/cart/Cart';
import { Checkout } from '@features/checkout/Checkout';
import { PaymentRedirect, CheckoutSuccess } from '@features/checkout/PaymentCallbacks';
import { Orders } from '@features/orders/Orders';
import { OrderDetail } from '@features/orders/OrderDetail';
import { Invoices } from '@features/billing/Invoices';
import { Settings } from '@features/settings/Settings';
import { Facilities } from '@features/facilities/Facilities';
import { FacilityDetail } from '@features/facilities/FacilityDetail';
import { FacilityMyBookings } from '@features/facilities/FacilityMyBookings';
import { SurveysList } from '@features/surveys/SurveysList';
import { SurveyDetail } from '@features/surveys/SurveyDetail';
import { SurveyResponseView } from '@features/surveys/SurveyResponseView';
import { MyResponsesList } from '@features/surveys/MyResponsesList';
import { Pets } from '@features/pets/Pets';
import { PetForm } from '@features/pets/PetForm';
import { Vehicles } from '@features/vehicles/Vehicles';
import { VehicleForm } from '@features/vehicles/VehicleForm';
import { Faq } from '@features/faq/Faq';
import { JoinCommunity } from '@features/join/JoinCommunity';

/**
 * Routes mirror `app-routing.module.ts` from the Angular member portal.
 * Auth/onboarding/public flows live above `<TabShell>`; everything under
 * `/app/*` is authenticated and renders inside the tab shell.
 */
function RequireAuth(): JSX.Element {
  const isAuthed = useAuthStore((s) => !!(s.accessToken || s.refreshToken));
  const navigate = useNavigate();
  // Expose React Router's client-side navigate to non-React callers (push tap,
  // axios interceptors). Without this, navigateTo() falls back to
  // window.location.href, which a Capacitor local-assets build can't resolve for
  // deep paths (no index.html rewrite) — so notification deep-links did nothing.
  useEffect(() => {
    setGlobalNavigator((path, options) => navigate(path, options));
  }, [navigate]);
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RedirectIfAuthed(): JSX.Element {
  const isAuthed = useAuthStore((s) => !!s.accessToken);
  if (isAuthed) return <Navigate to="/app" replace />;
  return <Outlet />;
}

function ResumeFlowFromParam(): JSX.Element {
  const { id, surveyId } = useParams<{ id: string; surveyId: string }>();
  const location = useLocation();
  const isEformRoute = location.pathname.includes('/eforms/');
  const listRoute = isEformRoute ? '/app/eforms' : '/app/surveys';

  if (!id) {
    return <Navigate to={listRoute} replace />;
  }

  const params = new URLSearchParams({
    instance: id,
    context: 'survey',
    kind: isEformRoute ? 'eform' : 'survey',
    title: isEformRoute ? 'eForm' : 'Survey',
    backTo: isEformRoute
      ? (surveyId ? `/app/eforms/${surveyId}` : '/app/eforms')
      : (surveyId ? `/app/surveys/${surveyId}` : '/app/surveys'),
  });

  return <Navigate to={`/survey/run?${params.toString()}`} replace />;
}

function withRouteBanners(moduleKey: ResidentBannerModuleKey, element: JSX.Element): JSX.Element {
  return <RouteBannerFrame moduleKey={moduleKey}>{element}</RouteBannerFrame>;
}

/**
 * Onboarding gate — the React equivalent of the Angular `OnboardingGuard`.
 * Before the member enters the app shell, check `/members/me/onboarding-gate`;
 * if onboarding is still required, redirect into the flow runner. Fails open
 * (renders the app) on error so a gate outage never locks members out.
 */
function OnboardingGate(): JSX.Element {
  const isAuthed = useAuthStore((s) => !!(s.accessToken || s.refreshToken));
  const gateQuery = useQuery({
    queryKey: ['onboarding-gate'],
    queryFn: fetchMyOnboardingGate,
    enabled: isAuthed,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (gateQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-paper">
        <span className="h-8 w-8 animate-spin rounded-pill border-2 border-hairline border-t-primary-600" />
      </div>
    );
  }

  const route = gateQuery.data ? onboardingRouteForGate(gateQuery.data) : null;
  if (route) return <Navigate to={route} replace />;

  return <TabShell />;
}

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/welcome" replace /> },

  /* ── Auth ─────────────────────────────────────────────────────── */
  {
    element: <RedirectIfAuthed />,
    children: [
      /* Welcome + primary auth screens are full-bleed / self-contained. */
      { path: '/welcome', element: <Welcome /> },
      { path: '/login', element: <Login /> },
      { path: '/signup', element: <Signup /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <AuthShell><ResetPassword /></AuthShell> },
    ],
  },

  /* Onboarding — runs in auth shell; runner handles unauth case */
  { path: '/onboarding', element: <AuthShell><Onboarding /></AuthShell> },
  { path: '/onboarding/:id', element: <AuthShell><Onboarding /></AuthShell> },

  /* Survey / eForm runner — dedicated runner (shared flow engine, survey variant) */
  { path: '/survey/run', element: <AuthShell><SurveyRunner /></AuthShell> },

  /* Public access invites — no auth required */
  { path: '/access/invite/:token', element: <AuthShell><PublicAccessInviteAccept /></AuthShell> },
  { path: '/access/onboarding/:accessInviteId/:accessToken', element: <AuthShell><PublicAccessOnboarding /></AuthShell> },

  /* Public commerce/community aliases kept for Angular deep-link parity */
  { path: '/events', element: withRouteBanners('events', <Events />) },
  { path: '/products', element: <Navigate to="/products/search" replace /> },
  { path: '/products/search', element: withRouteBanners('products', <Products />) },
  { path: '/products/:id', element: withRouteBanners('products', <ProductDetail />) },
  { path: '/cart', element: withRouteBanners('cart', <Cart />) },
  { path: '/checkout', element: withRouteBanners('checkout', <Checkout />) },

  /* Payment + tokenization callbacks */
  { path: '/tokenization/netcash-callback', element: <TokenizationCallback /> },
  { path: '/checkout/paynow/callback', element: <PaymentRedirect /> },
  { path: '/checkout/payment/redirect', element: <PaymentRedirect /> },
  { path: '/checkout/payment', element: <PaymentRedirect /> },
  { path: '/checkout/success', element: <CheckoutSuccess /> },

  /* ── Authenticated tab shell ──────────────────────────────────── */
  {
    element: <RequireAuth />,
    children: [
      /* First-run flow after account creation — tour → pick residence. */
      { path: '/tour', element: <WelcomeTour /> },
      { path: '/select-property', element: <SelectProperty /> },
      /* Join another community (SSO root scope + build-config gated). */
      { path: '/join', element: <JoinCommunity /> },
      {
        path: '/app',
        element: <OnboardingGate />,
        children: [
          { index: true, element: <Navigate to="home" replace /> },
          { path: 'home', element: <Home /> },
          { path: 'dashboard', element: <Home /> },
          { path: 'more', element: withRouteBanners('more', <MorePage />) },

          /* Book — facilities & bookings */
          { path: 'facilities', element: withRouteBanners('facilities', <Facilities />) },
          { path: 'facilities/my-bookings', element: withRouteBanners('facilities-bookings', <FacilityMyBookings />) },
          { path: 'facilities/:facilityId', element: withRouteBanners('facilities', <FacilityDetail />) },

          /* Community */
          { path: 'events', element: withRouteBanners('events', <Events />) },
          { path: 'classifieds', element: withRouteBanners('classifieds', <Classifieds />) },
          { path: 'classifieds/new', element: withRouteBanners('classifieds', <ClassifiedForm />), handle: { hideTabBar: true } },
          { path: 'classifieds/:id', element: withRouteBanners('classifieds', <ClassifiedDetail />) },
          { path: 'classifieds/:id/edit', element: withRouteBanners('classifieds', <ClassifiedForm />), handle: { hideTabBar: true } },
          { path: 'gallery', element: withRouteBanners('gallery', <GalleryLanding />) },
          { path: 'gallery/:id', element: withRouteBanners('gallery', <GalleryAlbum />) },
          { path: 'contact-groups', element: withRouteBanners('contact-groups', <ContactGroups />) },
          { path: 'directory', element: withRouteBanners('directory', <Directory />) },
          { path: 'suppliers', element: withRouteBanners('suppliers', <Suppliers />) },
          { path: 'news-stands', element: withRouteBanners('newsstand', <NewsStandList />) },
          { path: 'news-stands/:newsstandId', element: withRouteBanners('newsstand', <NewsStandDetail />) },
          {
            path: 'news-stands/:newsstandId/articles/:articleId/view',
            element: withRouteBanners('newsstand', <PdfViewerPage />),
            handle: { hideTabBar: true },
          },
          { path: 'surveys', element: withRouteBanners('surveys', <SurveysList />) },
          { path: 'surveys/:id', element: withRouteBanners('surveys', <SurveyDetail />) },
          { path: 'surveys/:surveyId/fill/:id', element: <ResumeFlowFromParam /> },
          { path: 'surveys/:surveyId/responses', element: withRouteBanners('surveys', <MyResponsesList />) },
          { path: 'surveys/:surveyId/response/:responseId', element: withRouteBanners('surveys', <SurveyResponseView />) },
          { path: 'eforms/:id', element: withRouteBanners('eforms', <SurveyDetail />) },
          { path: 'eforms/:surveyId/fill/:id', element: <ResumeFlowFromParam /> },
          { path: 'pets', element: withRouteBanners('pets', <Pets />) },
          { path: 'pets/new', element: withRouteBanners('pets', <PetForm />), handle: { hideTabBar: true } },
          { path: 'pets/:petId/edit', element: withRouteBanners('pets', <PetForm />), handle: { hideTabBar: true } },
          { path: 'vehicles', element: withRouteBanners('vehicles', <Vehicles />) },
          { path: 'vehicles/new', element: withRouteBanners('vehicles', <VehicleForm />), handle: { hideTabBar: true } },
          { path: 'vehicles/:vehicleId/edit', element: withRouteBanners('vehicles', <VehicleForm />), handle: { hideTabBar: true } },

          /* Estate */
          { path: 'map', element: withRouteBanners('map', <MapView />) },
          { path: 'management', element: withRouteBanners('management', <Management />) },
          { path: 'documents', element: withRouteBanners('documents', <Documents />) },
          { path: 'documents/:documentId/view', element: withRouteBanners('documents', <PdfViewerPage />), handle: { hideTabBar: true } },
          { path: 'iframe', element: withRouteBanners('iframe', <IframePage />) },

          /* Support */
          { path: 'support/requests', element: withRouteBanners('support', <SupportList />) },
          { path: 'support/requests/new', element: withRouteBanners('support', <NewSupportRequest />), handle: { hideTabBar: true } },
          { path: 'support/requests/:id', element: withRouteBanners('support', <SupportDetail />) },
          { path: 'support/chat', element: withRouteBanners('support', <SupportLiveChat />) },
          { path: 'member-requests', element: <MemberRequests /> },

          /* Commerce */
          { path: 'products', element: withRouteBanners('products', <Products />) },
          { path: 'products/search', element: withRouteBanners('products', <Products />) },
          { path: 'products/:id', element: withRouteBanners('products', <ProductDetail />) },
          { path: 'cart', element: withRouteBanners('cart', <Cart />) },
          { path: 'checkout', element: withRouteBanners('checkout', <Checkout />) },
          { path: 'orders', element: withRouteBanners('orders', <Orders />) },
          { path: 'orders/:orderId', element: withRouteBanners('orders', <OrderDetail />) },
          { path: 'invoices', element: withRouteBanners('billing', <Invoices />) },

          /* Account */
          { path: 'profile', element: withRouteBanners('profile', <Profile />) },
          { path: 'my-properties', element: withRouteBanners('properties', <MyProperties />) },
          { path: 'utilities', element: withRouteBanners('utilities', <MyUtilities />) },
          { path: 'utilities/:linkId', element: withRouteBanners('utilities', <UtilityDetail />) },
          { path: 'settings', element: withRouteBanners('settings', <Settings />) },
          { path: 'faq', element: withRouteBanners('faq', <Faq />) },

          /* Safety */
          { path: 'panic', element: withRouteBanners('panic', <PanicSos />) },
          { path: 'sos', element: withRouteBanners('panic', <PanicSos />) },
          { path: 'panic/contacts', element: <Navigate to="/app/panic?tab=contacts" replace /> },
          { path: 'panic/contacts/new', element: withRouteBanners('panic', <PanicContactForm />), handle: { hideTabBar: true } },
          { path: 'panic/contacts/:contactId/edit', element: withRouteBanners('panic', <PanicContactForm />), handle: { hideTabBar: true } },

          /* Access invites */
          { path: 'access-invites', element: withRouteBanners('access-management', <AccessInvitesList />) },
          { path: 'access-invites/new', element: withRouteBanners('access-management', <NewAccessInvitePage />) },
          { path: 'my-visitors', element: withRouteBanners('access-management', <MyVisitors />) },
          { path: 'access-invites/:id/selfie', element: <Navigate to="/app/access-invites" replace /> },
        ],
      },
    ],
  },

  { path: '*', element: <Navigate to="/welcome" replace /> },
]);
